import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge, { STATUS_META } from "@/components/StatusBadge";
import Timeline from "@/components/Timeline";
import PortalBadge, { Route } from "@/components/PortalBadge";
import BookingDialog from "@/components/BookingDialog";
import { Package, Plus, Truck, CheckCircle2, Search, MapPin, FileText, Tag, CalendarClock, Printer, Download, Link2, Clock } from "lucide-react";
import { toast } from "@/lib/toast";
import { InvoiceView, AwbLabel, ProformaInvoice } from "@/components/ShipmentDocs";
import StatCard from "@/components/StatCard";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ListSkeleton from "@/components/ListSkeleton";
import TrackingPill from "@/components/TrackingPill";
import { triggerHaptic } from "@/lib/haptics";

export default function UserDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const [pickupDate, setPickupDate] = useState("");
  const [docOrder, setDocOrder] = useState(null);
  const [docType, setDocType] = useState("invoice");

  const openDoc = (order, type) => { setDocType(type); setDocOrder(order); };
  const schedulePickup = async (order) => {
    if (!pickupDate) { toast.error("Pick a date first"); return; }
    try {
      const { data } = await api.patch(`/orders/${order.id}/pickup`, { pickup_date: pickupDate });
      toast.success("Pickup scheduled");
      setSelected(data);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openFile = async (id) => {
    try {
      const { data } = await api.get(`/files/${id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch { toast.error("Unable to open file"); }
  };

  const copyTrackLink = (o) => {
    const url = `${window.location.origin}/track?tn=${o.tracking_number}`;
    navigator.clipboard?.writeText(url);
    toast.success("Tracking link copied");
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await api.get("/orders", { params });
      setOrders(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const stats = {
    total: orders.length,
    active: orders.filter((o) => !["DELIVERED", "CANCELLED"].includes(o.status)).length,
    delivered: orders.filter((o) => o.status === "DELIVERED").length,
  };

  return (
    <div className="space-y-6" data-testid="user-dashboard">
      <PageHeader eyebrow="My Shipments" tone="emerald" title="Track & Ship" subtitle="Book new shipments, follow every parcel door to door and download your documents."
        actions={<><Button size="lg" className="shine-hover" onClick={() => setOpen(true)} data-testid="new-order-button"><Plus className="h-4 w-4" /> New Booking</Button><BookingDialog open={open} onOpenChange={setOpen} onCreated={load} /></>} />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Total Orders" value={stats.total} icon={Package} tone="blue" delay={0} testId="stat-total-orders" />
        <StatCard label="Active" value={stats.active} icon={Truck} tone="cyan" delay={1} testId="stat-active" />
        <StatCard label="Delivered" value={stats.delivered} icon={CheckCircle2} tone="emerald" delay={2} testId="stat-delivered" />
      </div>

      {/* Filters */}
      <div className="panel flex flex-wrap gap-2 p-3">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-1 gap-2 min-w-[240px]">
          <div className="relative flex-1 glow-ring">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search tracking / name…" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="user-order-search" />
          </div>
          <Button type="submit" variant="outline">Search</Button>
        </form>
        <Select value={statusFilter} onValueChange={(v) => { triggerHaptic("tap"); setStatusFilter(v); }}>
          <SelectTrigger className="h-10 w-[170px] rounded-[10px]" data-testid="user-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            {Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Orders */}
      <div className="panel overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 border-b border-border bg-slate-50/70 px-5 py-2.5 text-[11px] font-mono-label font-semibold uppercase tracking-widest text-muted-foreground">
          <div className="col-span-3">Tracking</div>
          <div className="col-span-4">Destination</div>
          <div className="col-span-1">Price</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <ListSkeleton rows={5} />
        ) : orders.length === 0 ? (
          <EmptyState icon={Package} title="No shipments yet" hint="Create your first booking and we'll track it door to door." testId="orders-empty" className="rounded-none border-0 shadow-none"
            action={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New Booking</Button>} />
        ) : (
          orders.map((o) => (
            <div key={o.id} className="group flex flex-col gap-2 border-b border-border/60 px-4 py-4 transition-colors last:border-0 hover:bg-primary/[.025] md:grid md:grid-cols-12 md:items-center md:gap-4 md:px-5" data-testid={`order-row-${o.tracking_number}`}>
              <div className="flex items-center justify-between md:col-span-3 md:block">
                <TrackingPill tn={o.tracking_number} />
                <span className="md:hidden"><StatusBadge status={o.status} testId={`status-${o.tracking_number}`} /></span>
              </div>
              <div className="text-sm md:col-span-4">
                <div className="font-semibold">{o.recipient_name}</div>
                <div className="flex items-center gap-1 truncate text-xs text-muted-foreground"><MapPin className="h-3 w-3 shrink-0" /> {o.delivery_address}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <PortalBadge portal={o.source_portal} testId={`portal-${o.tracking_number}`} />
                  <Route from={o.origin_country} to={o.destination_country} />
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm md:col-span-1 md:block">
                {o.quoted_price ? <span className="font-semibold text-foreground">₹{o.quoted_price.toLocaleString("en-IN")}</span> : <span className="text-xs text-muted-foreground">—</span>}
              </div>
              <div className="hidden md:col-span-2 md:block"><StatusBadge status={o.status} /></div>
              <div className="flex gap-2 md:col-span-2 md:justify-end">
                <Button size="sm" variant="outline" className="flex-1 md:flex-none" onClick={() => { setSelected(o); setPickupDate(o.pickup_date || ""); }} data-testid={`view-order-${o.tracking_number}`}>
                  <FileText className="h-4 w-4" /> Open
                </Button>
                <Button size="sm" className="flex-1 md:flex-none" onClick={() => navigate(`/track?tn=${o.tracking_number}`)} data-testid={`track-order-${o.tracking_number}`}>
                  <MapPin className="h-4 w-4" /> Track
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight text-2xl flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" /> {selected.tracking_number}
                </DialogTitle>
              </DialogHeader>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm">
                  <div className="font-medium">{selected.recipient_name}</div>
                  <div className="text-xs text-muted-foreground">{selected.delivery_address}</div>
                </div>
                <StatusBadge status={selected.status} />
              </div>
              {selected.boxes && selected.boxes.length > 0 && (
                <div className="rounded-lg border border-border bg-secondary/40 p-3 mb-3 text-sm space-y-1">
                  <div className="font-semibold">{selected.box_count} box{selected.box_count === 1 ? "" : "es"} · {selected.weight} kg total</div>
                  {selected.boxes.map((b, i) => (
                    <div key={i} className="text-xs text-muted-foreground">
                      Box {i + 1}: {b.length}×{b.width}×{b.height} cm · {b.weight} kg
                      {b.items && b.items.length > 0
                        ? ` · ${b.items.map((it) => `${it.name} x${it.quantity}`).join(", ")}`
                        : (b.item_content ? ` · ${b.item_content}` : "")}
                    </div>
                  ))}
                </div>
              )}
              <div className="rounded-lg border border-border p-3 mb-3 space-y-3">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-muted-foreground">AWB No.</span><div className="font-semibold" data-testid="detail-awb">{selected.awb_number || "—"}</div></div>
                  <div><span className="text-muted-foreground">Invoice No.</span><div className="font-semibold">{selected.invoice_number || "—"}</div></div>
                </div>
                {selected.commercial_invoice_number && (
                  <div className="text-xs"><span className="text-muted-foreground">Commercial Invoice No.</span><div className="font-semibold" data-testid="detail-commercial-invoice">{selected.commercial_invoice_number}</div></div>
                )}
                {selected.documents && selected.documents.length > 0 && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">Attached Documents</span>
                    <div className="mt-1 space-y-1" data-testid="detail-documents">
                      {selected.documents.map((d) => (
                        <button key={d.id} type="button" onClick={() => openFile(d.id)} className="flex items-center gap-2 text-primary hover:underline w-full text-left" data-testid={`detail-doc-${d.id}`}>
                          <Download className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{d.original_filename}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  {selected.status !== "Booked" ? (
                    <div className="flex items-center gap-2 text-sm text-emerald-600" data-testid="pickup-completed">
                      <CheckCircle2 className="h-4 w-4" /> Pickup completed{selected.pickup_date ? ` on ${selected.pickup_date}` : ""}
                    </div>
                  ) : (
                    <>
                      <div className="text-xs text-muted-foreground mb-1">Schedule Pickup</div>
                      <div className="flex gap-2">
                        <Input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} data-testid="pickup-date-input" />
                        <Button size="sm" onClick={() => schedulePickup(selected)} data-testid="schedule-pickup-button">
                          <CalendarClock className="h-4 w-4 mr-1" /> {selected.pickup_scheduled ? "Update" : "Schedule"}
                        </Button>
                      </div>
                      {selected.pickup_scheduled && <div className="text-xs text-emerald-600 mt-1" data-testid="pickup-confirmed">Pickup scheduled for {selected.pickup_date}</div>}
                    </>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => openDoc(selected, "invoice")} data-testid="open-invoice-button"><FileText className="h-4 w-4 mr-1" /> Invoice</Button>
                  <Button size="sm" variant="outline" onClick={() => openDoc(selected, "proforma")} data-testid="open-proforma-button"><FileText className="h-4 w-4 mr-1" /> Proforma</Button>
                  <Button size="sm" variant="outline" onClick={() => openDoc(selected, "awb")} data-testid="open-awb-button"><Tag className="h-4 w-4 mr-1" /> AWB Label</Button>
                  <Button size="sm" variant="outline" onClick={() => copyTrackLink(selected)} data-testid="copy-track-link-button"><Link2 className="h-4 w-4 mr-1" /> Copy Link</Button>
                </div>
              </div>
              <Timeline timeline={selected.timeline} status={selected.status} order={selected} publicView />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Document dialog (Invoice / AWB) */}
      <Dialog open={!!docOrder} onOpenChange={(v) => !v && setDocOrder(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="doc-dialog">
          <DialogHeader className="no-print">
            <DialogTitle className={docType === "awb" ? "sr-only" : "font-display tracking-tight text-2xl"}>
              {docType === "invoice" ? "Invoice" : docType === "proforma" ? "Proforma Invoice" : "Air Waybill Label"}
            </DialogTitle>
          </DialogHeader>
          {docOrder && (docType === "invoice" ? <InvoiceView order={docOrder} /> : docType === "proforma" ? <ProformaInvoice order={docOrder} /> : <AwbLabel order={docOrder} />)}
          <DialogFooter className="no-print">
            <Button onClick={() => window.print()} data-testid="print-doc-button">
              <Printer className="h-4 w-4 mr-1.5" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
